import {
  TextNode,
  $applyNodeReplacement,
  type NodeKey,
  type EditorConfig,
  type SerializedTextNode,
  type Spread,
} from 'lexical';

export type DynamicTagType = 'field' | 'assignment';

export type SerializedDynamicTagNode = Spread<
  {
    tagType: DynamicTagType;
    fieldIds?: string[];
    fieldContext?: string;
    value?: string;
    label: string;
  },
  SerializedTextNode
>;

export class DynamicTagNode extends TextNode {
  __tagType: DynamicTagType;
  __fieldIds?: string[];
  __fieldContext?: string;
  __value?: string;
  __label: string;

  static getType(): string {
    return 'dynamic-tag';
  }

  static clone(node: DynamicTagNode): DynamicTagNode {
    return new DynamicTagNode(
      {
        tagType: node.__tagType,
        fieldIds: node.__fieldIds,
        fieldContext: node.__fieldContext,
        value: node.__value,
        label: node.__label,
      },
      node.__key,
    );
  }

  constructor(
    data: {
      tagType: DynamicTagType;
      fieldIds?: string[];
      fieldContext?: string;
      value?: string;
      label: string;
    },
    key?: NodeKey,
  ) {
    super(data.label, key);
    this.__tagType = data.tagType;
    this.__fieldIds = data.fieldIds;
    this.__fieldContext = data.fieldContext;
    this.__value = data.value;
    this.__label = data.label;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.textTransform = 'capitalize';
    dom.style.borderRadius = '4px';
    dom.style.background = '#F4F6FF';
    dom.style.padding = '0 4px';
    dom.style.fontSize = '12px';
    dom.style.color = '#4B4B4B';
    dom.style.display = 'inline-block';
    dom.style.userSelect = 'none';
    dom.className = 'dynamic-tag';
    return dom;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    return super.updateDOM(prevNode, dom, config);
  }

  static importJSON(serializedNode: SerializedDynamicTagNode): DynamicTagNode {
    const node = $createDynamicTagNode({
      tagType: serializedNode.tagType,
      fieldIds: serializedNode.fieldIds,
      fieldContext: serializedNode.fieldContext,
      value: serializedNode.value,
      label: serializedNode.label,
    });
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): SerializedDynamicTagNode {
    return {
      ...super.exportJSON(),
      type: 'dynamic-tag',
      tagType: this.__tagType,
      fieldIds: this.__fieldIds,
      fieldContext: this.__fieldContext,
      value: this.__value,
      label: this.__label,
    };
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  isTextEntity(): true {
    return true;
  }

  // Getters for reading node data
  getTagType(): DynamicTagType {
    return this.__tagType;
  }

  getFieldIds(): string[] | undefined {
    return this.__fieldIds;
  }

  getFieldContext(): string | undefined {
    return this.__fieldContext;
  }

  getValue(): string | undefined {
    return this.__value;
  }

  getLabel(): string {
    return this.__label;
  }
}

export function $createDynamicTagNode(data: {
  tagType: DynamicTagType;
  fieldIds?: string[];
  fieldContext?: string;
  value?: string;
  label: string;
}): DynamicTagNode {
  const node = new DynamicTagNode(data);
  node.setMode('token');
  return $applyNodeReplacement(node);
}

export function $isDynamicTagNode(node: unknown): node is DynamicTagNode {
  return node instanceof DynamicTagNode;
}
